import { useMountEffect } from './useMountEffect'
import { useState } from 'react'
import { useSnackbarContext } from './../components/Context/SnackbarContext'
import { fetchAcademicYears } from '../api/communities'
import { FetchAcademicYearsResponseAcademicYears } from '../swagger'
import { SnackbarSeverity } from '../components/Alerts/SnackbarAlert'
import { extractedErrorObject } from '../api/swagger'
import useLoadingContext from './useLoadingContext'
import { LoadingContext } from '../components/Context/LoadingContext'
import React from 'react'
import { OperationIds } from '../swagger/operationIdEnum'
import { useLearningCenterContext } from '../components/Context/LearningCenterContext'

/** This hook fetching academicYears,
 *  buildAcademicYearsFilterOptions function parse the array that come from the API
 * and left it ready for the dropdown component */

//Setting this value add up 0 for avoiding fetch twice at the beginning
const defaultAcademicYear = 0
export interface AcademicYears {
  year: number
  name: string
  isDefault: boolean
}
interface UseAcademicYears {
  selectedAcademicYear: number
  academicYears: Array<AcademicYears>
  setSelectedAcademicYear: (academicYear: number) => void
  loadingId: string
}

export interface UseAcademicYearsProps {
  //isMounted: () => boolean
  errorMessage?: string
  isLC?: boolean
  defaultAY?: number
  /**
   * In case where we are calling this requires permissions to view, don't call
   * the endpoint.
   */
  preventFetch?: boolean
}

export const useAcademicYears = ({
  errorMessage,
  isLC,
  defaultAY,
  preventFetch = false,
}: UseAcademicYearsProps): UseAcademicYears => {
  const [academicYears, setAcademicYears] = useState<Array<AcademicYears>>([])
  const [selectedAcademicYear, setSelectedAcademicYear] =
    useState<number>(defaultAcademicYear)
  const { setSnackbarMessage, setSnackbarSeverity, setSnackbarState } =
    useSnackbarContext()
  const { addLoadingIds } = React.useContext(LoadingContext)
  const loadingId = OperationIds.FetchAcademicYears
  const { updateAcademicYear } = useLearningCenterContext()

  const fetchAcademicYear = async () => {
    try {
      let requestParam = {}
      if (isLC) {
        requestParam = { isLC: isLC }
      }
      const academicYearsFromAPI = await fetchAcademicYears(requestParam)
      if (academicYearsFromAPI.academicYears.length > 0) {
        /* include isDefault = true if the property is not present on the endpoint's response
         * temporary fix since the endpoint response should include this property for more info on BE fix WF-1368024
         */
        const includesIsDefaultProperty = Object.keys(
          academicYearsFromAPI.academicYears[0]
        ).includes('isDefault')
        const includesIsCurrentProperty = Object.keys(
          academicYearsFromAPI.academicYears[0]
        ).includes('isCurrent')
        if (!includesIsDefaultProperty && !includesIsCurrentProperty) {
          academicYearsFromAPI.academicYears[0].isDefault = true
        }
        const academicYearFilterOptions = buildAcademicYearsFilterOptions(
          academicYearsFromAPI.academicYears ?? []
        )

        setAcademicYears(academicYearFilterOptions)
        if (
          !defaultAY ||
          !academicYearsFromAPI.academicYears.some(
            (year) => year.year === defaultAY
          )
        ) {
          if (
            academicYearsFromAPI.academicYears?.some(
              (academicYears) => academicYears?.isDefault
            )
          ) {
            const academicYear = academicYearsFromAPI.academicYears?.filter(
              (academicYear) => academicYear.isDefault
            )[0].year
            setSelectedAcademicYear(academicYear)
            if (isLC) {
              updateAcademicYear(academicYear)
            }
          } else {
            const academicYear = academicYearsFromAPI.academicYears?.filter(
              (academicYear) => academicYear.isCurrent
            )[0].year
            setSelectedAcademicYear(academicYear)
            if (isLC) {
              updateAcademicYear(academicYear)
            }
          }
        } else {
          setSelectedAcademicYear(defaultAY)
        }
      }
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'Unknown',
        message: (e as unknown as Error).message ?? errorMessage,
      }
      setSnackbarMessage?.(errorObject.message)
      setSnackbarSeverity?.(SnackbarSeverity.Error)
      setSnackbarState?.(true)
    }
  }

  useMountEffect(() => {
    if (!preventFetch) {
      addLoadingIds([loadingId])
    }
  })

  useLoadingContext({
    asyncFunction: fetchAcademicYear,
    loadingId,
  })

  return {
    academicYears,
    selectedAcademicYear,
    setSelectedAcademicYear,
    loadingId,
  }
}

export const buildAcademicYearsFilterOptions = (
  academicYears: FetchAcademicYearsResponseAcademicYears[]
): Array<{ year: number; name: string; isDefault: boolean }> => {
  const academicYearMap = academicYears
    .sort((a, b) => b.year - a.year)
    .map((it) => {
      return {
        year: it.year,
        isDefault: it.isDefault ?? false,
        name: it.isCurrent
          ? [it.year, '(CURRENT)'].join(' ')
          : it.year.toString(),
      }
    })

  return academicYearMap
}
